"use client";
import { useState } from "react";
import ButtonAccount from "@/components/login/ButtonAccount";
import { cn } from "@/libs/utils";
import { usePathname } from "next/navigation";
import LoginModal from "@/components/modals/LoginModal";
import { UserContext } from "@/context/UserContext";
import { useContext } from "react";

/**
 * 登录按钮组件
 * 
 * @param {boolean} mobileStyle - 是否使用移动端样式，默认为 false
 * @param {boolean} avatarOnly - 是否只显示头像图标，默认为 false
 * @param {boolean} fullWidth - 按钮是否占满容器宽度，默认为 false
 * @param {string} className - 自定义类名，可选
 * @param {Function} onLoginClick - 登录按钮点击回调函数，可选
 * @returns {JSX.Element} - 返回登录按钮或账户按钮组件
 */
const ButtonSignin = ({ 
  mobileStyle = false, 
  avatarOnly = false,
  fullWidth = false,
  className = "", 
  onLoginClick
}) => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { user, fetchUserInfo } = useContext(UserContext);
  const pathname = usePathname();

  // 如果用户已登录，显示账户按钮
  if (user) {
    return (
      <div className={cn("flex items-center", fullWidth && "w-full")}>
        <ButtonAccount 
          supabaseUser={user} 
          mobileStyle={mobileStyle} 
          avatarOnly={avatarOnly} 
          fullWidth={fullWidth}
          className={className}
        />
      </div>
    );
  }

  /**
   * 处理登录按钮点击事件
   * 如果提供了 onLoginClick 回调，则调用回调
   * 并打开登录模态框
   */
  const handleClick = () => {
    if (onLoginClick) {
      onLoginClick();
    }
    setShowLoginModal(true);
  };

  // 用户未登录，显示登录按钮
  return (
    <>
      <button
        onClick={handleClick}
        className={cn(
          "inline-flex items-center justify-center transition-all duration-300",
          avatarOnly 
                  ? "w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-sm hover:shadow-[0_0_15px_rgba(37,99,235,0.4)] hover:scale-105"
      : "px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-md hover:-translate-y-0.5 hover:shadow-[0_0_15px_rgba(37,99,235,0.5)]",
          fullWidth && "w-full",
          className
        )}
        aria-label={avatarOnly ? "登录账户" : undefined}
        title={avatarOnly ? "登录账户" : undefined}
      >
        {avatarOnly ? (
          // 如果 avatarOnly 为 true，则显示用户图标
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        ) : (
          // 否则显示"Login"文本
          <span>Login</span>
        )}
      </button>

      {/* 登录模态框 */}
      {showLoginModal && (
        <LoginModal 
          isOpen={showLoginModal} 
          onClose={() => setShowLoginModal(false)}
          redirectTo={pathname}
          onSuccess={() => {
            setShowLoginModal(false);
            fetchUserInfo && fetchUserInfo();
          }}
        />
      )}
    </>
  );
};

export default ButtonSignin;
